<script setup lang="ts">
import { CustomMarker } from 'vue3-google-map';
import Typography from '@components/Typography.vue';
import { type GoogleMapLatLngLiteral, MapMarkerType } from '@global-types/googleMap.ts';
import { computed } from 'vue';

interface MarkerProps {
  position: GoogleMapLatLngLiteral,
  isHighlighted?: boolean,
  zIndex?: number,
  type?: MapMarkerType,
  markerClass?: string,
}

const {
  isHighlighted = false,
  zIndex = 0,
  type = MapMarkerType.PRIMARY,
  markerClass = '',
} = defineProps<MarkerProps>();

const computedMarkerClass = computed<string | null>(() => {
  if (type !== MapMarkerType.SECONDARY) {
    return null;
  }

  if (isHighlighted) {
    return 'flex';
  }

  return 'hidden group-hover:flex';
});
</script>

<template>
  <CustomMarker
    :options="{
      position,
      anchorPoint: 'BOTTOM_CENTER',
      zIndex: isHighlighted ? 200 : zIndex,
    }"
  >
    <Typography
      as="div"
      variant="body-small-bold"
      class="marker group"
      :class="{ 'marker--highlighted': isHighlighted }"
    >
      <div
        :class="[
          markerClass,
          computedMarkerClass,
          'px-2 py-1',
        ]"
      >
        <slot />
      </div>
    </Typography>
  </CustomMarker>
</template>

<style scoped lang="scss">
@mixin markerHighlighted {
  @apply bg-gray-darker;

  @apply border-gray-darker;

  @apply text-white;
}

.marker {
  @apply border border-solid border-gray rounded-lg shadow-[0_4px_8px_0_#00000059];

  @apply min-h-4 min-w-6;

  @apply bg-white;

  @apply overflow-hidden;

  @apply cursor-pointer;

  &--highlighted {
    @include markerHighlighted;
  }

  @media (hover: hover) {
    &:hover {
      @include markerHighlighted;
    }
  }
}
</style>
